import { Field } from '@atlaskit/form';
import { Stack, Text } from '@atlaskit/primitives';
import FieldPicker from '@databases/field-picker/FieldPicker';
import JiraFieldPicker from '@databases/field-picker/JiraFieldPicker';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseFieldOptional,
  getDatabaseFields
} from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type JiraIssueOptions = { value: string; label: string };

type JiraDetailsFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>;
};

const isJiraIssueField = (
  field: DatabaseField | undefined
): field is DatabaseField<DatabaseFieldType.JIRA_ISSUE> => {
  return field?.type === DatabaseFieldType.JIRA_ISSUE;
};

const JiraDetailsFieldSettings: FunctionComponent<JiraDetailsFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const [sourceField, sourceFields] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraftWithoutDeleted = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: false
    });
    const selectedSourceField = getDatabaseFieldOptional(
      databaseDraftWithoutDeleted,
      field.settings.fieldId
    );

    return [
      isJiraIssueField(selectedSourceField) ? selectedSourceField : undefined,
      getDatabaseFields(databaseDraftWithoutDeleted).filter(isJiraIssueField)
    ] as const;
  });

  const handleSourceFieldChange = useCallback(
    (value: DatabaseField | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_FIELD_ID,
          fieldId: field.id,
          settingsFieldId: value?.id
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleJiraFieldChange = useCallback(
    (value: JiraIssueOptions | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_JIRA_FIELD,
          fieldId: field.id,
          jiraField: value?.value
        })
      );

      if (value?.label) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_FIELD_NAME,
            fieldId: field.id,
            name: value.label,
            delta: [{ delete: field.name.length }, { insert: value.label }]
          })
        );
      }
    },
    [contentId, dispatch, field.id, field.name]
  );

  return (
    <Stack space="space.050">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.jiraDetailsDisplaySourceLabel)}
          </Text>
        }
        id="jira-details-source-field-picker-input"
        name="jira-details-source-field"
        isRequired
      >
        {({ fieldProps }) => (
          <FieldPicker
            fields={sourceFields}
            value={sourceField}
            onChange={handleSourceFieldChange}
            isRequired
            placeholder={formatMessage(
              intlMessages['field-settings.type-jira-issue-selector-placeholder']
            )}
            spacing="compact"
            inputId={fieldProps.id}
            autoSelectFirst
          />
        )}
      </Field>

      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.jiraDetailsDisplayedLabel)}
          </Text>
        }
        id="jira-details-field-picker-input"
        name="jira-details-field"
        isRequired
      >
        {({ fieldProps }) => (
          <JiraFieldPicker
            cloudId={sourceField?.settings.cloudId}
            datasourceType="jira"
            value={field.settings.jiraField}
            onChange={handleJiraFieldChange}
            isDisabled={!sourceField}
            isRequired
            isCompact
            inputId={fieldProps.id}
            showHelperMessage={false}
          />
        )}
      </Field>
    </Stack>
  );
};

export default JiraDetailsFieldSettings;
